/**
 * `/docs` — in-app documentation site.
 *
 * Two content kinds share one split layout:
 *   - Reference: every `@localagent/sdk` resource and its methods, rendered
 *     from the SDK's bundled `DocsIndex` (generated from TSDoc — no network
 *     call; the content ships inside the SDK).
 *   - Guides: narrative markdown authored in repo-root `docs/guides/*.md`,
 *     mirrored into `./docs/guides.ts` (see that file for why).
 *
 * Each method shows a safety badge (read / write / destructive) so users see
 * at a glance which operations mutate or destroy data — the same
 * classification Shogun's action layer enforces.
 *
 * Lives in app-shell so web and desktop both pick it up via the shared
 * router (`routes/index.tsx`), same pattern as `/search`.
 */

import { useMemo, useState } from 'react';
import { DocsIndex } from '@localagent/sdk';
import type { MethodDocs, MethodSafety } from '@localagent/sdk';
import { Badge } from '@localagent/ui/components/badge';
import type { BadgeProps } from '@localagent/ui/components/badge';
import { Input } from '@localagent/ui/components/input';
import { ListDetailLayout } from '@localagent/ui/components/list-detail-layout';
import { MarkdownView } from '@localagent/ui/components/viewers/MarkdownView';
import { cn } from '@localagent/ui/lib/utils';
import { GUIDES } from './docs/guides';

// read = safe (green), write = mutates (amber), destructive = irreversible (red).
const SAFETY_VARIANT: Record<MethodSafety, BadgeProps['variant']> = {
  read: 'success',
  write: 'warning',
  destructive: 'destructive',
};

function SafetyBadge({ safety }: { safety: MethodSafety }) {
  return (
    <Badge
      variant={SAFETY_VARIANT[safety]}
      className="text-[10px]"
      data-testid={`docs-safety-${safety}`}
      title={
        safety === 'read'
          ? 'Read-only — does not change data'
          : safety === 'write'
            ? 'Write — creates or modifies data'
            : 'Destructive — deletes data or cannot be undone'
      }
    >
      {safety}
    </Badge>
  );
}

type Selection =
  | { kind: 'guide'; slug: string }
  | { kind: 'resource'; name: string };

function selectionId(sel: Selection): string {
  return sel.kind === 'guide' ? `guide:${sel.slug}` : `resource:${sel.name}`;
}

function MethodCard({ name, docs }: { name: string; docs: MethodDocs }) {
  const params = Object.entries(docs.params);
  return (
    <div
      id={`docs-method-${name}`}
      data-testid={`docs-method-${name}`}
      className="rounded-md bg-card p-4"
    >
      <div className="flex items-center gap-2">
        <code className="text-sm font-semibold">{name}</code>
        <SafetyBadge safety={docs.safety} />
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{docs.description}</p>
      {params.length > 0 && (
        <div className="mt-3">
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Parameters
          </h4>
          <table className="w-full text-xs">
            <tbody>
              {params.map(([param, desc]) => (
                <tr key={param} className="border-b last:border-0">
                  <td className="w-48 py-1 pr-3 align-top font-mono">{param}</td>
                  <td className="py-1 text-muted-foreground">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-3">
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Returns
        </h4>
        <code className="text-xs text-muted-foreground">{docs.returns}</code>
      </div>
      {docs.example && (
        <div className="mt-3">
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Example
          </h4>
          <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">
            <code>{docs.example}</code>
          </pre>
        </div>
      )}
      {docs.related && docs.related.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          Related: {docs.related.map(r => <code key={r} className="mr-2">{r}</code>)}
        </p>
      )}
    </div>
  );
}

export function DocsView() {
  // The docs content is generated at SDK build time and bundled — a plain
  // in-memory index, so constructing it once per mount is all that's needed.
  const docsIndex = useMemo(() => new DocsIndex(), []);
  const resourceNames = useMemo(() => docsIndex.resourceNames(), [docsIndex]);

  const [selection, setSelection] = useState<Selection>(
    () => GUIDES.length > 0
      ? { kind: 'guide', slug: GUIDES[0].slug }
      : { kind: 'resource', name: resourceNames[0] ?? '' },
  );
  const [query, setQuery] = useState('');

  // Non-empty query switches the sidebar to method search results (powered
  // by DocsIndex.search — names, descriptions, params). Clearing it returns
  // to the browsable guide + resource lists.
  const searchResults = useMemo(
    () => (query.trim() ? docsIndex.search(query).slice(0, 30) : []),
    [docsIndex, query],
  );

  const selectedGuide = selection.kind === 'guide'
    ? GUIDES.find(g => g.slug === selection.slug)
    : undefined;
  const selectedResource = selection.kind === 'resource'
    ? docsIndex.resource(selection.name)
    : undefined;

  const sidebarItemClass = (active: boolean) =>
    cn(
      'w-full rounded-md px-2.5 py-1.5 text-left text-sm transition-colors',
      active
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
    );

  const listContent = (
    <div className="flex flex-col gap-4 p-4">
      <Input
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search methods…"
        data-testid="docs-search-input"
        className="h-8 text-xs"
      />
      {query.trim() ? (
        <div className="flex flex-col gap-0.5" data-testid="docs-search-results">
          {searchResults.length === 0 && (
            <p className="px-2.5 py-1.5 text-xs text-muted-foreground">
              No methods match &quot;{query}&quot;
            </p>
          )}
          {searchResults.map(result => (
            <button
              key={result.name}
              type="button"
              data-testid={`docs-search-result-${result.name}`}
              className="w-full rounded-md px-2.5 py-1.5 text-left transition-colors hover:bg-accent"
              onClick={() => {
                setSelection({ kind: 'resource', name: result.resource });
                setQuery('');
                // Let the detail pane render before jumping to the method.
                requestAnimationFrame(() => {
                  document
                    .getElementById(`docs-method-${result.method}`)
                    ?.scrollIntoView({ block: 'start' });
                });
              }}
            >
              <span className="flex items-center gap-2">
                <code className="text-xs font-medium">{result.name}</code>
                <SafetyBadge safety={result.docs.safety} />
              </span>
              <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                {result.docs.description}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <>
          <div>
            <h2 className="mb-1 px-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Guides
            </h2>
            <div className="flex flex-col gap-0.5">
              {GUIDES.map(guide => (
                <button
                  key={guide.slug}
                  type="button"
                  data-testid={`docs-guide-${guide.slug}`}
                  className={sidebarItemClass(
                    selection.kind === 'guide' && selection.slug === guide.slug,
                  )}
                  onClick={() => setSelection({ kind: 'guide', slug: guide.slug })}
                >
                  {guide.title}
                </button>
              ))}
            </div>
          </div>
          <div>
            <h2 className="mb-1 px-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              SDK Reference
            </h2>
            <div className="flex flex-col gap-0.5">
              {resourceNames.map(name => {
                const resource = docsIndex.resource(name);
                const methodCount = resource ? Object.keys(resource.methods).length : 0;
                return (
                  <button
                    key={name}
                    type="button"
                    data-testid={`docs-resource-${name}`}
                    className={cn(
                      sidebarItemClass(
                        selection.kind === 'resource' && selection.name === name,
                      ),
                      'flex items-center justify-between',
                    )}
                    onClick={() => setSelection({ kind: 'resource', name })}
                  >
                    <span className="font-mono">{name}</span>
                    <span className="text-xs opacity-70">{methodCount}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );

  const detailContent = (
    <div className="flex flex-col gap-4 p-6" data-testid="docs-detail">
      {selectedGuide && (
        <div data-testid={`docs-guide-body-${selectedGuide.slug}`}>
          <MarkdownView content={selectedGuide.body} />
        </div>
      )}
      {selection.kind === 'resource' && (
        selectedResource ? (
          <>
            <div>
              <h2 className="text-lg font-semibold font-mono">{selection.name}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {selectedResource.description}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {Object.entries(selectedResource.methods).map(([methodName, methodDocs]) => (
                <MethodCard key={methodName} name={methodName} docs={methodDocs} />
              ))}
            </div>
          </>
        ) : (
          // resourceNames() and resource() read the same index, so this only
          // occurs if the generated content is empty — surface it, don't hide it.
          <p className="text-sm text-destructive">
            No documentation found for resource &quot;{selection.name}&quot;.
          </p>
        )
      )}
    </div>
  );

  return (
    <div className="flex h-full flex-col" data-testid="docs-view">
      <header className="border-b bg-background px-6 py-4">
        <h1 className="text-2xl font-semibold">Documentation</h1>
        <p className="text-sm text-muted-foreground">
          Guides and the complete SDK reference — every operation with its
          read / write / destructive safety classification
        </p>
      </header>
      <ListDetailLayout
        layout="split"
        listWidth="300px"
        selectedId={selectionId(selection)}
        listContent={listContent}
        detailContent={detailContent}
        className="flex-1 min-h-0"
      />
    </div>
  );
}
